import type { ImmutableModelFailure } from "./model.ts";
import { Cause, Context, Deferred, Effect, Exit, Ref, Scope } from "effect";
import { ImmutableModelAdmissionFailure, type ImmutableAcquire, type ImmutableLease, type ImmutableRegistry } from "./registry-model.ts";

interface Ready<Model> {
  readonly _tag: "Ready";
  readonly token: object;
  readonly model: Model;
  readonly contents: string;
  readonly scope: Scope.Closeable;
  readonly references: number;
}
interface Pending<Model, E> {
  readonly _tag: "Pending";
  readonly token: object;
  readonly contents: string;
  readonly done: Deferred.Deferred<Exit.Exit<Ready<Model>, E | ImmutableModelAdmissionFailure>>;
}
type Entry<Model, E> = Ready<Model> | Pending<Model, E>;
type Admission<Model, E> =
  | { readonly _tag: "Borrow"; readonly entry: Ready<Model> }
  | { readonly _tag: "Wait"; readonly entry: Pending<Model, E> }
  | { readonly _tag: "Create"; readonly entry: Pending<Model, E> }
  | { readonly _tag: "Refuse"; readonly error: ImmutableModelAdmissionFailure };

const CreationAncestors = Context.Reference<readonly object[]>("workerstation/frontend/ModelCreationAncestors", { defaultValue: () => [] });

const appendCleanup = <E>(primary: Cause.Cause<E>, cleanup: Exit.Exit<void>): Exit.Exit<never, E> =>
  Exit.failCause(cleanup._tag === "Success" ? primary : Cause.fromReasons([...primary.reasons, ...cleanup.cause.reasons]));

export const makeImmutableRegistry = Effect.fn("Frontend.makeImmutableRegistry")(function* <Model, E = ImmutableModelFailure>(owner: Scope.Scope, onReleaseFailure?: (cause: Cause.Cause<never>) => Effect.Effect<void>) {
  const entries = yield* Ref.make<ReadonlyMap<string, Entry<Model, E>>>(new Map());
  const life = yield* Ref.make({ closed: false, active: 0 });
  const drained = yield* Deferred.make<void>();
  const finish = Effect.gen(function* () {
    const value = yield* Ref.updateAndGet(life, state => ({ ...state, active: state.active - 1 }));
    if (value.closed && value.active === 0) yield* Deferred.succeed(drained, undefined);
  });
  const begin = (acquiring: boolean) => Ref.modify(life, state =>
    state.closed || acquiring && owner.state._tag === "Closed"
      ? [false, state] as const
      : [true, { ...state, active: state.active + 1 }] as const);
  const releaseEntry = (key: string, token: object): Effect.Effect<void> => Effect.gen(function* () {
    const scope = yield* Ref.modify(entries, current => {
      const entry = current.get(key);
      if (!entry || entry._tag !== "Ready" || entry.token !== token) return [undefined, current];
      const next = new Map(current);
      if (entry.references > 1) { next.set(key, { ...entry, references: entry.references - 1 }); return [undefined, next]; }
      next.delete(key);
      return [entry.scope, next];
    });
    if (scope) yield* Scope.close(scope, Exit.void);
  });
  const lease = (key: string, entry: Ready<Model>) => Effect.gen(function* () {
    const released = yield* Ref.make(false);
    const release = Effect.uninterruptible(Effect.gen(function* () {
      if (yield* Ref.getAndSet(released, true)) return;
      if (!(yield* begin(false))) return;
      yield* releaseEntry(key, entry.token).pipe(
        Effect.onExit(exit => onReleaseFailure && Exit.isFailure(exit) ? onReleaseFailure(exit.cause) : Effect.void),
        Effect.ensuring(finish)
      );
    }));
    return Object.freeze({ model: entry.model, release });
  });
  const raw = (request: ImmutableAcquire<Model, E>, key: string, contents: string): Effect.Effect<ImmutableLease<Model>, E | ImmutableModelAdmissionFailure, Scope.Scope> => Effect.gen(function* () {
    const ancestors = yield* CreationAncestors;
    const done = yield* Deferred.make<Exit.Exit<Ready<Model>, E | ImmutableModelAdmissionFailure>>();
    const pending: Pending<Model, E> = { _tag: "Pending", token: {}, contents, done };
    const admission = yield* Ref.modify(entries, (current): readonly [Admission<Model, E>, ReadonlyMap<string, Entry<Model, E>>] => {
      const entry = current.get(key);
      if (entry) {
        if (entry.contents !== contents) return [{ _tag: "Refuse", error: new ImmutableModelAdmissionFailure({ reason: "identity", identity: key }) }, current];
        if (entry._tag === "Pending") return [{ _tag: "Wait", entry }, current];
        const reserved = { ...entry, references: entry.references + 1 };
        const next = new Map(current); next.set(key, reserved);
        return [{ _tag: "Borrow", entry: reserved }, next];
      }
      const next = new Map(current); next.set(key, pending);
      return [{ _tag: "Create", entry: pending }, next];
    });
    if (admission._tag === "Refuse") return yield* admission.error;
    if (admission._tag === "Wait") {
      if (ancestors.includes(admission.entry.token)) return yield* new ImmutableModelAdmissionFailure({ reason: "creating", identity: key });
      yield* (yield* Deferred.await(admission.entry.done));
      return yield* raw(request, key, contents);
    }
    if (admission._tag === "Borrow") {
      const validation = yield* Effect.exit(Effect.suspend(() => request.validate ? request.validate(admission.entry.model) : Effect.void));
      if (validation._tag === "Failure") {
        const cleanup = yield* Effect.exit(releaseEntry(key, admission.entry.token));
        return yield* appendCleanup(validation.cause, cleanup);
      }
      return yield* lease(key, admission.entry);
    }
    const scope = yield* Scope.make();
    const created = yield* Effect.exit(request.create.pipe(Effect.provideService(Scope.Scope, scope), Effect.provideService(CreationAncestors, [...ancestors, pending.token])));
    if (created._tag === "Failure") {
      yield* Ref.update(entries, current => { const next = new Map(current); if (next.get(key)?.token === pending.token) next.delete(key); return next; });
      const cleanup = yield* Effect.exit(Scope.close(scope, Exit.void));
      const failure = appendCleanup(created.cause, cleanup);
      yield* Deferred.succeed(pending.done, failure);
      return yield* failure;
    }
    const entry: Ready<Model> = { _tag: "Ready", token: pending.token, model: created.value, contents, scope, references: 1 };
    yield* Ref.update(entries, current => { const next = new Map(current); next.set(key, entry); return next; });
    yield* Deferred.succeed(pending.done, Exit.succeed(entry));
    return yield* lease(key, entry);
  });
  const acquire: ImmutableRegistry<Model, E>["acquire"] = request => Effect.uninterruptible(Effect.gen(function* () {
    if (typeof request.key !== "string" || request.key.length < 1 || request.key.length > 4096) return yield* new ImmutableModelAdmissionFailure({ reason: "key" });
    if (typeof request.contents !== "string") return yield* new ImmutableModelAdmissionFailure({ reason: "contents" });
    const caller = yield* Effect.scope;
    if (caller.state._tag === "Closed" || !(yield* begin(true))) return yield* new ImmutableModelAdmissionFailure({ reason: "closed" });
    return yield* Effect.acquireRelease(raw(request, request.key, request.contents), lease => lease.release).pipe(
      Effect.flatMap(lease => caller.state._tag === "Closed" ? Effect.andThen(lease.release, new ImmutableModelAdmissionFailure({ reason: "closed" })) : Effect.succeed(lease)),
      Effect.ensuring(finish)
    );
  }));
  const inspect = Ref.get(entries).pipe(Effect.map(current => Object.freeze([...current].flatMap(([key, entry]) => entry._tag === "Ready" ? [Object.freeze({ key, references: entry.references })] : []).sort((a, b) => a.key < b.key ? -1 : a.key > b.key ? 1 : 0))));
  yield* Effect.addFinalizer(() => Effect.uninterruptible(Effect.gen(function* () {
    const state = yield* Ref.getAndUpdate(life, current => ({ ...current, closed: true }));
    if (state.active > 0) yield* Deferred.await(drained);
    const owned = yield* Ref.getAndSet(entries, new Map());
    const exits = yield* Effect.forEach([...owned.values()], entry => entry._tag === "Ready" ? Effect.exit(Scope.close(entry.scope, Exit.void)) : Effect.succeed(Exit.void));
    const reasons = exits.flatMap(exit => exit._tag === "Failure" ? exit.cause.reasons : []);
    if (reasons.length) return yield* Effect.failCause(Cause.fromReasons(reasons));
  })));
  return { acquire, inspect } satisfies ImmutableRegistry<Model, E>;
});
